import type { PickersTextFieldProps } from '@mui/x-date-pickers/PickersTextField';
import { PickersTextField } from '@mui/x-date-pickers/PickersTextField';
import type { ForwardRefExoticComponent, RefAttributes } from 'react';
import { forwardRef, useId } from 'react';
import { FieldLabelTab, resolveFieldState, StyledFieldRoot } from '../TextField/FieldLabelTab.tsx';

/**
 * PickerField — the field used by every DS date/time picker (wired as the pickers' `textField`
 * slot in the theme). It wraps MUI X's **`PickersTextField`** in the same label-tab chrome the DS
 * `TextField` uses (Figma "Data Picker" field).
 *
 * Why not reuse the DS `TextField` component directly? MUI X v9 removed the legacy single-`<input>`
 * field DOM (`enableAccessibleFieldDOMStructure` is gone) — the picker field MUST be a
 * `PickersTextField` (section-list based) for the accessible date editing to work. So we keep that
 * accessible field and graft the shared chrome on top: the tab markup and its whole state chain
 * come from {@link FieldLabelTab}, so the two fields cannot drift apart.
 *
 * **No read-only state here**, unlike the DS `TextField`: a non-editable date is shown with the
 * `ReadOnly` component instead (designer, 2026-08-13 — see DatePicker.usage.md). The `readOnly`
 * detection this file used to carry was dead code anyway, since MUI X never forwards `readOnly`
 * down to the `textField` slot.
 */
/** Public props type — PickerField accepts the full MUI X `PickersTextField` surface. */
export type PickerFieldProps = PickersTextFieldProps;

// Explicit annotation so the emitted .d.ts references the named `PickersTextFieldProps` union
// instead of trying (and failing) to name its non-portable internal constituents (TS2883).
export const PickerField: ForwardRefExoticComponent<PickersTextFieldProps & RefAttributes<HTMLDivElement>> = forwardRef<
  HTMLDivElement,
  PickersTextFieldProps
>(function PickerField(props, ref) {
  const { label, className, sx, slotProps: slotPropsProp, ...rest } = props;

  const labelId = useId();

  // Read (don't destructure) so these still flow to PickersTextField via `rest`.
  const fieldState = resolveFieldState({
    disabled: props.disabled,
    error: props.error,
    success: props.color === 'success',
  });

  return (
    <StyledFieldRoot
      ref={ref}
      className={className}
      fieldState={fieldState}
      fullWidth={Boolean(props.fullWidth)}
      sx={sx}
    >
      {label && <FieldLabelTab label={label} labelId={labelId} required={Boolean(props.required)} />}
      <PickersTextField
        {...rest}
        label={undefined}
        slotProps={{
          ...slotPropsProp,
          // The visual label lives in the tab above (MUI's own label is removed), so hand its
          // id to the section group — this is the field's ACCESSIBLE NAME (the `role="group"`
          // aria-labelledby; same pattern as TextField.tsx). PickersTextField spreads these
          // input slot props after its built-ins, so the id reliably wins.
          input: {
            ...(slotPropsProp?.input as object | undefined),
            ...(label ? { 'aria-labelledby': labelId } : {}),
          },
        }}
      />
    </StyledFieldRoot>
  );
});
